import { useContext, useEffect, useState } from 'react';
import { FormFieldValidationContext } from '../../components/form/context/FormFieldValidationContext';
import { FormFieldValue, NamedDependencies } from './types';
import { FormFieldValidator, ValidationResult } from './validator/FormFieldValidator';

function useFormFieldValidationInner(validator: FormFieldValidator, deps: NamedDependencies) {
  const [value, setValue] = useState<FormFieldValue>(deps.value);
  const validationContext = useContext(FormFieldValidationContext);
  const [isValid, setIsValid] = useState(validationContext.isValid);
  const [hasError, setHasError] = useState(validationContext.hasError);
  const [errorTags, setErrorTags] = useState(validationContext.errorTags);

  useEffect(() => {
    setValue(deps.value);
  }, [deps.value]);

  async function validate(): Promise<ValidationResult> {
    const validationResult = await validator.validate(deps.value);
    validationContext.setValidationState(
      validationResult.isValid,
      !validationResult.isValid,
      validationResult.errorTags,
    );
    return validationResult;
  }

  useEffect(() => {
    setIsValid(validationContext.isValid);
    setHasError(validationContext.hasError);
    setErrorTags(validationContext.errorTags);
  }, [validationContext]);

  return { isValid, hasError, errorTags, validate, value };
}

export function useFormFieldValidation(validator: FormFieldValidator, deps: NamedDependencies) {
  const { isValid, hasError, errorTags, validate, value } = useFormFieldValidationInner(
    validator,
    deps,
  );
  return { isValid, hasError, errorTags, validate, value };
}

export function useFormFieldMainValidation(validator: FormFieldValidator, deps: NamedDependencies) {
  const validationContext = useContext(FormFieldValidationContext);
  const { isValid, hasError, errorTags, validate, value } = useFormFieldValidationInner(
    validator,
    deps,
  );

  useEffect(() => {
    if (!!validate) {
      validationContext.setValidationMethod(validate);
    }
  }, [validationContext, value, validate]);

  return { isValid, hasError, errorTags, validate, value };
}

function useFormFieldValidationWithOnValueChangeValidationInner(
  validator: FormFieldValidator,
  value: FormFieldValue,
  shouldSetValidationStateAlways: boolean,
) {
  const validationContext = useContext(FormFieldValidationContext);
  useEffect(() => {
    validator.validate(value).then((validationResult) => {
      if (value.length && (shouldSetValidationStateAlways || validationResult.isValid)) {
        validationContext.setValidationState(
          validationResult.isValid,
          !validationResult.isValid,
          validationResult.errorTags,
        );
      }
    });
  }, [value]);
}

export function useFormFieldMainValidationWithOnValueChangeValidation(
  validator: FormFieldValidator,
  deps: NamedDependencies,
  shouldSetValidationStateAlways = false,
) {
  const { isValid, hasError, errorTags, validate, value } = useFormFieldMainValidation(
    validator,
    deps,
  );
  useFormFieldValidationWithOnValueChangeValidationInner(
    validator,
    value,
    shouldSetValidationStateAlways,
  );
  return { isValid, hasError, errorTags, validate, value };
}

export function useFormFieldValidationWithOnValueChangeValidation(
  validator: FormFieldValidator,
  deps: NamedDependencies,
  shouldSetValidationStateAlways = false,
) {
  const { isValid, hasError, errorTags, validate, value } = useFormFieldValidation(validator, deps);
  useFormFieldValidationWithOnValueChangeValidationInner(
    validator,
    value,
    shouldSetValidationStateAlways,
  );
  return { isValid, hasError, errorTags, validate, value };
}
